import { Tooltip } from "antd";
import dayjs from "dayjs";
import { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  IAppointment,
  IAppointmentTableData,
} from "../../../../@types/Appointment";
import { Notification } from "../../../../@types/Notification";
import { Delete } from "../../../../assets/icons/Delete";
import { ReactComponent as NoAppointmentsImage } from "../../../../assets/images/woman-computer.svg";
import { Button } from "../../../../components/Button";
import { Table } from "../../../../components/Table";
import { TableSkeleton } from "../../../../components/TableSkeleton";
import { TooltipLine } from "../../../../components/TooltipLine";
import { appointmentsService } from "../../../../services/appointmentsService";
import { getLongFormattedDate } from "../../../../utils/dateFormatter";
import { mixpanelActions } from "../../../../utils/mixpanel";
import { getSafeHttpUrl, openExternalUrl } from "../../../../utils/url";
import {
  AppointmentsContainer,
  DeleteAppointmentButton,
  EmptyStateImageContainer,
  NoAppointmentsMessage,
  TagContainer,
} from "./AppointmentsTable.styles";
import { appointmentsTableColumns } from "./appointmentsTableColumns";
import { DeleteAppointmentModal } from "./components/DeleteAppointmentModal";

type AppointmentsTableProps = {
  appointments?: IAppointment[];
  isLoading: boolean;
  // The standalone /appointments page lets the table draw its own border; the
  // patient-page section sets this to false because its panel already has one.
  bordered?: boolean;
  emptyStateMessage?: string;
  onAppointmentDelete: (
    notification?: Notification,
    refetchTable?: boolean
  ) => void;
};

export function AppointmentsTable({
  appointments,
  isLoading,
  bordered = true,
  emptyStateMessage,
  onAppointmentDelete,
}: AppointmentsTableProps) {
  const { t } = useTranslation();

  // EN-5191: resolved here rather than as a default parameter — a default cannot call the
  // hook, and the caller may still pass its own message.
  const invalidUrlMessage = t("appointments_noValidLink");
  const noAppointmentsMessage =
    emptyStateMessage ?? t("appointments_emptyState");
  const [isDeleteAppointmentModalOpen, setIsDeleteAppointmentModalOpen] =
    useState(false);
  const [appointmentToDelete, setAppointmentToDelete] =
    useState<IAppointment>();

  const parsedAppointments = useMemo(() => {
    if (!appointments) return [];

    const handleAction = (appointment: IAppointment) => {
      const currentDate = dayjs();
      const appointmentDate = dayjs(appointment.date);

      // Appointments on a previous day can no longer be joined: show the
      // Join button disabled at 38% opacity (per design). Today's and future
      // appointments stay active/joinable.
      if (appointmentDate.isBefore(currentDate, "day")) {
        return (
          <TagContainer>
            <Button
              label={t("appointments_join")}
              type="button"
              size="small"
              disabled
              opacity={0.38}
            />
          </TagContainer>
        );
      }

      // The stored URL is untrusted (EN-5095): the API used to accept any URI
      // scheme, so legacy rows can still hold `javascript:`/`data:` payloads,
      // and the hardened read paths blank bad URLs out to "" / null. Resolve a
      // safe http(s) URL up front — never hand an unchecked value to
      // window.open. The HCP URL takes precedence when usable, otherwise the
      // clinician falls back to the patient URL.
      const joinUrl =
        getSafeHttpUrl(appointment.hcpAppointmentUrl) ??
        getSafeHttpUrl(appointment.appointmentUrl);

      if (joinUrl === undefined) {
        return (
          <TagContainer>
            <Tooltip
              title={<TooltipLine>{invalidUrlMessage}</TooltipLine>}
              arrow={false}
              color="#4B4B4B"
              mouseLeaveDelay={0}
              styles={{
                body: {
                  padding: "16px 20px",
                  borderRadius: "4px !important",
                },
              }}
            >
              {/* antd needs a non-disabled wrapper to receive the hover that
                  opens the tooltip: a disabled button emits no pointer events. */}
              <span>
                <Button
                  label={t("appointments_join")}
                  type="button"
                  size="small"
                  disabled
                  opacity={0.38}
                  ariaLabel={invalidUrlMessage}
                />
              </span>
            </Tooltip>
          </TagContainer>
        );
      }

      return (
        <TagContainer>
          <Button
            label={t("appointments_join")}
            type="button"
            size="small"
            onClick={() => {
              openExternalUrl(joinUrl);
              mixpanelActions.track("User Action: JoinAppointmentButton");
            }}
          />
        </TagContainer>
      );
    };

    const handleDeleteAppointment = (appointment: IAppointment) => {
      setIsDeleteAppointmentModalOpen(true);
      setAppointmentToDelete(appointment);
    };

    return appointments.map((appointment: IAppointment) => {
      return {
        id: appointment.id,
        title: appointment.title,
        date: getLongFormattedDate(appointment.date),
        duration: `${appointment.durationInMinutes} min`,
        createdBy: appointment.createdByEmail,
        appointmentStatus: handleAction(appointment),
        deleteAppointment: (
          <DeleteAppointmentButton
            role="button"
            onClick={() => {
              handleDeleteAppointment(appointment);
              mixpanelActions.track("User Action: CancelAppointmentButton");
            }}
            onKeyDown={() => handleDeleteAppointment(appointment)}
            tabIndex={-1}
            className="actionContainer"
          >
            <Delete fillColor="#EB5A7B" />
          </DeleteAppointmentButton>
        ),
      } as IAppointmentTableData;
    });
  }, [appointments, t]);

  const isDeletingRef = useRef(false);

  const handleOnModaClose = async (confirm: boolean) => {
    if (confirm && appointmentToDelete?.id) {
      if (isDeletingRef.current) return;
      isDeletingRef.current = true;

      try {
        const result = await appointmentsService.deleteAppointment(
          appointmentToDelete?.id
        );

        const notification: Notification = {
          show: true,
          message: "",
          type: "success",
          width: "max-content",
        };

        let refetchTable = false;

        if (result.status >= 200 && result.status < 300) {
          notification.message = t("appointments_toasts_canceled");
          refetchTable = true;
          onAppointmentDelete(notification, refetchTable);
        } else {
          notification.message = t("appointments_toasts_cancelFailed");
          notification.type = "error";
          onAppointmentDelete(notification, refetchTable);
        }
      } finally {
        isDeletingRef.current = false;
      }
    }

    setIsDeleteAppointmentModalOpen(false);
    setAppointmentToDelete(undefined);
  };

  if (isLoading) {
    return (
      <TableSkeleton columnsNumber={6} rowsNumber={6} bordered={bordered} />
    );
  }

  if (parsedAppointments.length === 0) {
    return (
      <EmptyStateImageContainer bordered={bordered}>
        <NoAppointmentsImage width={181} height={158} />
        <NoAppointmentsMessage>{noAppointmentsMessage}</NoAppointmentsMessage>
      </EmptyStateImageContainer>
    );
  }

  return (
    <>
      <AppointmentsContainer>
        <Table
          data={parsedAppointments}
          columns={appointmentsTableColumns}
          hasBorder={bordered}
        />
      </AppointmentsContainer>

      {isDeleteAppointmentModalOpen ? (
        <DeleteAppointmentModal onClose={handleOnModaClose} />
      ) : null}
    </>
  );
}
